import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Layers, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tile = ({ to, onClick, icon: Icon, title, subtitle }) => {
  const body = (
    <>
      <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors shrink-0">
        <Icon className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="font-medium text-sm truncate group-hover:text-[#003366]">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </>
  );
  const cls = 'flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[#003366] hover:shadow-md transition-all group w-full';
  return to ? <Link to={to} className={cls}>{body}</Link> : <button type="button" onClick={onClick} className={cls}>{body}</button>;
};

const Skeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}
  </div>
);

/** Browse by sector: admin-configured sectors -> subsectors -> jobs (normal search page filtered by category + subcategory). */
export default function JobSectorsPage() {
  const [sectors, setSectors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subsectors, setSubsectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/jobs/sectors`)
      .then((res) => setSectors(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load sectors. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const openSector = (s) => {
    // A sector with no subsectors goes straight to its jobs.
    if (!s.subsector_count) return;
    setSelected(s);
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/api/jobs/sectors/${encodeURIComponent(s.slug || s.name)}/subsectors`)
      .then((res) => setSubsectors(res.data.subsectors || []))
      .catch(() => { setSubsectors([]); setError('Could not load subsectors for this sector.'); })
      .finally(() => setLoading(false));
  };

  const back = () => { setSelected(null); setSubsectors([]); setError(''); };
  const sectorJobsLink = (s) => `/jobs?category=${encodeURIComponent(s.slug || s.name)}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        {selected && (
          <button type="button" onClick={back} className="inline-flex items-center gap-1 text-sm text-[#003366] mb-3 hover:underline">
            <ArrowLeft className="w-4 h-4" /> All sectors
          </button>
        )}
        <h1 className="text-3xl font-bold text-foreground">{selected ? selected.name : 'Browse Jobs by Sector'}</h1>
        <p className="text-muted-foreground mt-1">
          {selected ? 'Choose a subsector to see its jobs' : 'Choose a sector, then a subsector, to see the jobs available'}
        </p>
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {loading ? <Skeleton /> : !selected ? (
        sectors.length === 0 ? <p className="text-muted-foreground">No sectors available right now.</p> : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sectors.map((s) => {
              const subtitle = `${(s.job_count || 0).toLocaleString()} jobs${s.subsector_count ? ` · ${s.subsector_count} subsectors` : ''}`;
              return s.subsector_count
                ? <Tile key={s.id} onClick={() => openSector(s)} icon={Briefcase} title={s.name} subtitle={subtitle} />
                : <Tile key={s.id} to={sectorJobsLink(s)} icon={Briefcase} title={s.name} subtitle={subtitle} />;
            })}
          </div>
        )
      ) : (
        <>
          <div className="mb-4">
            <Link to={sectorJobsLink(selected)} className="text-sm text-[#003366] hover:underline">
              View all {selected.name} jobs →
            </Link>
          </div>
          {subsectors.length === 0 ? <p className="text-muted-foreground">No subsectors in this sector yet.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {subsectors.map((sub) => (
                <Tile key={sub.id}
                  to={`${sectorJobsLink(selected)}&subcategory=${encodeURIComponent(sub.slug || sub.name)}`}
                  icon={Layers} title={sub.name} subtitle={`${(sub.job_count || 0).toLocaleString()} jobs`} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
